import { useQuery } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { listSessions } from './api';
import { formatBytes, relativeTime } from './format';

const POLL_MS = 30_000;

/**
 * The audit trail: one row per relay channel — who reached which box, when,
 * and how many bytes moved. Metadata only; the broker never sees plaintext.
 */
export function SessionsTable() {
  const sessions = useQuery({
    queryKey: ['sessions'],
    queryFn: listSessions,
    refetchInterval: POLL_MS,
  });

  if (sessions.isLoading) {
    return (
      <Box sx={{ display: 'grid', placeItems: 'center', p: 6 }}>
        <CircularProgress />
      </Box>
    );
  }
  if (sessions.isError) {
    return (
      <Alert severity="error">
        {sessions.error instanceof Error ? sessions.error.message : 'could not load sessions'}
      </Alert>
    );
  }
  if (!sessions.data || sessions.data.length === 0) {
    return (
      <Box sx={{ p: 6, textAlign: 'center' }}>
        <Typography color="text.secondary">No sessions logged yet.</Typography>
      </Box>
    );
  }

  return (
    <Paper elevation={1}>
      <TableContainer>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Device</TableCell>
              <TableCell>User</TableCell>
              <TableCell>Kind</TableCell>
              <TableCell>Started</TableCell>
              <TableCell>Duration</TableCell>
              <TableCell align="right">Sent</TableCell>
              <TableCell align="right">Received</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sessions.data.map((s) => (
              <TableRow key={s.id} hover>
                <TableCell sx={{ fontWeight: 500 }}>{s.deviceName ?? s.deviceId}</TableCell>
                <TableCell>{s.username ?? '—'}</TableCell>
                <TableCell>
                  <Chip
                    label={s.kind}
                    size="small"
                    variant="outlined"
                    color={s.kind === 'ssh' ? 'primary' : 'default'}
                  />
                  {/* 'file' alone does not say what happened — the detail is
                      the operation and the path ("fetch C:\MES\report.pdf").
                      Truncated because a deep path would push the byte columns
                      off screen; the full value is in the title. */}
                  {s.detail && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      title={s.detail}
                      sx={{
                        display: 'block',
                        fontFamily: 'monospace',
                        maxWidth: 320,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        direction: 'rtl', // keep the filename visible, clip the left
                        textAlign: 'left',
                      }}
                    >
                      {s.detail}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <span title={new Date(s.startedAt).toLocaleString()}>
                    {relativeTime(s.startedAt)}
                  </span>
                </TableCell>
                <TableCell>{durationLabel(s.startedAt, s.endedAt)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {formatBytes(s.bytesUp)}
                </TableCell>
                <TableCell align="right" sx={{ fontFamily: 'monospace' }}>
                  {formatBytes(s.bytesDown)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function durationLabel(startedAt: string, endedAt: string | null): string {
  if (!endedAt) return 'open';
  const seconds = Math.max(0, Math.round((Date.parse(endedAt) - Date.parse(startedAt)) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`;
}
